import { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import styled from '@emotion/styled'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import BreakWordTypography from 'components/common/BreakWordTypography'
import NowrapDiv from 'components/common/NowrapDiv'
import { FunctionModal } from 'components/common/Modal'
import { Tabs, Tab, TabsWrapper } from 'components/common/AllServiceModal/Tabs'
import {
  Wrapper,
  ScrollableWrapper,
  StickyWrapper,
  ServiceClassificationWrapper,
  ServiceIconWrapper,
  LogoImageWrapper,
} from 'components/common/AllServiceModal/ModalComponent'
import Link from 'components/common/A'

const DEFAULT_SERVICELOGO = '/img/default_service_icon.svg'

const a11yProps = (index) => {
  return {
    id: `scrollable-tab-${index}`,
    'aria-controls': `scrollable-tab-panel-${index}`,
  }
}

const IconButtonWrapper = styled(IconButton)`
  ${({ theme }) => `
  > .MuiSvgIcon-root {
    fill: ${theme.palette.text.minus2};
    font-size: 24px;
  }
`}
`

const HoverCardPoint = styled(Box)`
  ${({ theme, $hovered }) => `
    height: 4px;
    width: 4px;
    border-radius: 50%;
    background-color: ${$hovered ? theme.palette.primary.main : 'transparent'};
    position: absolute;
    left: 50%;
    bottom: -8px;
    transform: translate(-50%, 0);
  `}
`

// 服務方案區塊
const ServiceClassificationCard = ({ item }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const [hovered, setHovered] = useState(false)

  return (
    <Box gridColumn={{ xs: 'span 3', md: 'span 2' }} sx={{ cursor: 'pointer' }}>
      <Link
        $origin
        href={`/service/${item.serviceClassification?.identityCode}`}
      >
        <ServiceIconWrapper
          onMouseEnter={() => isDesktop && setHovered(true)}
          onMouseLeave={() => isDesktop && setHovered(false)}
        >
          <LogoImageWrapper $hovered={hovered}>
            <img
              src={
                item?.serviceClassification?.entranceIcon?.url ||
                DEFAULT_SERVICELOGO
              }
              width="48px"
              height="48px"
            />
            <HoverCardPoint $hovered={hovered} />
          </LogoImageWrapper>
          <NowrapDiv $lines={2} $textCenter={true}>
            {item?.serviceClassification?.name}
          </NowrapDiv>
        </ServiceIconWrapper>
      </Link>
    </Box>
  )
}

// 子目錄區塊
const ServiceClassificationList = ({
  subCatalog,
  isShowSubTitle,
  name,
  isFirst,
}) => {
  const showCatalogItem = subCatalog?.items?.filter((item) => {
    return item?.type === 'serviceClassification'
  })
  if (showCatalogItem?.length <= 0) return null

  return (
    <ServiceClassificationWrapper $isFirst={isFirst}>
      {isShowSubTitle && (
        <BreakWordTypography
          mb={4}
          variant="label"
          color="textSecondary"
          fontWeight="fontWeightBold"
        >
          {name}
        </BreakWordTypography>
      )}
      <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={4}>
        {subCatalog?.map((item) => {
          return <ServiceClassificationCard key={item._id} item={item} />
        })}
      </Box>
    </ServiceClassificationWrapper>
  )
}

const ShowAllServiceModal = ({ open, onClose, catalogsData }) => {
  const [tabIndex, setTabIndex] = useState(0)
  const [catalogItems, setCatalogItems] = useState([])
  const [showCatalogItems, setShowCatalogItems] = useState([])
  const [subCatalogName, setSubCatalogName] = useState([])

  const onTabChange = (e, newTabIndex) => {
    setTabIndex(newTabIndex)
  }

  useEffect(() => {
    const getCatalogData = async () => {
      setCatalogItems(
        catalogsData.map((catalogData) => {
          return catalogData?.subcatalogs
        })
      )
    }
    if (catalogsData?.length) getCatalogData()
  }, [catalogsData])

  useEffect(() => {
    const setShowItems = async () => {
      const subCatalogs =
        catalogItems?.[tabIndex]?.sort((a, b) => b.sort - a.sort) || []
      subCatalogs?.forEach((subCatalog) => {
        const serviceItem = subCatalog.items?.filter((item) => {
          return item.type === 'serviceClassification'
        })
        if (serviceItem?.length > 0) {
          setSubCatalogName((subCatalogName) => [
            ...subCatalogName,
            subCatalog?.name,
          ])
          setShowCatalogItems((showCatalogItems) => [
            ...showCatalogItems,
            serviceItem,
          ])
        }
      })
    }
    if (catalogsData?.length) setShowItems()

    return () => {
      setShowCatalogItems([])
      setSubCatalogName([])
    }
  }, [catalogItems, tabIndex])

  return (
    <FunctionModal open={open} onClose={onClose} ModalWrapper={Wrapper}>
      <StickyWrapper mb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" color="textMinus2">
            選擇預約服務
          </Typography>
          <IconButtonWrapper onClick={onClose} size="large">
            <CloseIcon />
          </IconButtonWrapper>
        </Box>
        {catalogsData?.length > 1 && (
          <TabsWrapper>
            <Tabs
              value={tabIndex}
              onChange={onTabChange}
              scrollButtons
              aria-label="image tabs"
            >
              {catalogsData?.map((catalog, index) => (
                <Tab key={index} label={catalog?.name} {...a11yProps(index)} />
              ))}
            </Tabs>
          </TabsWrapper>
        )}
      </StickyWrapper>
      <ScrollableWrapper>
        {showCatalogItems?.map((item, index) => {
          return (
            <ServiceClassificationList
              key={index}
              name={subCatalogName[index]}
              subCatalog={item}
              isShowSubTitle={showCatalogItems?.length > 1}
              isFirst={index < 1}
            />
          )
        })}
      </ScrollableWrapper>
    </FunctionModal>
  )
}

export default ShowAllServiceModal
